import React, { useEffect, useRef, useState } from "react";
import { IEventSummary, IScore, IShow } from "@eagerdog/interfaces";
import { buildNonLicensedPayload } from "@eagerdog/scoring-core";
import { AxiosError } from "axios";

import { apiService } from "src/services/api.service";
import { toast } from "src/components/Toast/ToastManager";

import { AwardDropdown, PlacementGrid, SaveNote } from "./awardFields";
import { preferScores } from "./dogLabel";
import { seedNonLicensed } from "./storedResults";

import styles from "./UKCConformationResults.module.scss";

interface IProps {
  level: "novice_puppy" | "veteran";
  title: string;
  breed: string;
  breedGroup: string;
  scores: IScore[];
  event: IEventSummary;
  show: IShow;
  onSaved: () => void;
}

type SaveStatus = "idle" | "saving" | "saved" | "error";

const NonLicensedBreedCard: React.FC<IProps> = ({ level, title, breed, breedGroup, scores, event, show, onSaved }) => {
  const males = scores.filter((score) => score.dog_sex === "Male");
  const females = scores.filter((score) => score.dog_sex === "Female");
  const seeded = seedNonLicensed(show, breed, breedGroup, level, scores);

  const [malePlacements, setMalePlacements] = useState<string[]>(seeded.male);
  const [femalePlacements, setFemalePlacements] = useState<string[]>(seeded.female);
  const [bestOfBreed, setBestOfBreed] = useState(seeded.bestOfBreed);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");

  const formRef = useRef({ male: seeded.male, female: seeded.female, bestOfBreed: seeded.bestOfBreed });
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedStatusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (saveTimerRef.current) return;
    const next = seedNonLicensed(show, breed, breedGroup, level, scores);
    setMalePlacements(next.male);
    setFemalePlacements(next.female);
    setBestOfBreed(next.bestOfBreed);
    formRef.current = { male: next.male, female: next.female, bestOfBreed: next.bestOfBreed };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const triggerAutoSave = () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (savedStatusTimerRef.current) clearTimeout(savedStatusTimerRef.current);
    setSaveStatus("idle");
    saveTimerRef.current = setTimeout(async () => {
      setSaveStatus("saving");
      const current = formRef.current;
      try {
        await apiService.saveConformationNonLicensedBreedResults(
          event._id,
          show._id,
          buildNonLicensedPayload({
            level,
            breed,
            breedGroup,
            male: current.male,
            female: current.female,
            bestOfBreed: current.bestOfBreed,
          })
        );
        setSaveStatus("saved");
        onSaved();
        savedStatusTimerRef.current = setTimeout(() => setSaveStatus("idle"), 2000);
      } catch (error) {
        setSaveStatus("error");
        toast.show({
          title: "Error",
          content: `Failed to save ${title} results.`,
          type: "fail",
          errorDetails: error as AxiosError,
        });
      } finally {
        saveTimerRef.current = null;
      }
    }, 800);
  };

  const changeMale = (index: number, id: string) => {
    setMalePlacements((prev) => {
      const next = [...prev];
      next[index] = id;
      formRef.current.male = next;
      return next;
    });
    triggerAutoSave();
  };

  const changeFemale = (index: number, id: string) => {
    setFemalePlacements((prev) => {
      const next = [...prev];
      next[index] = id;
      formRef.current.female = next;
      return next;
    });
    triggerAutoSave();
  };

  const changeBob = (id: string) => {
    setBestOfBreed(id);
    formRef.current.bestOfBreed = id;
    triggerAutoSave();
  };

  return (
    <div className={styles.breedCard}>
      <div className={styles.breedCardHeader}>
        <span className={styles.breedName}>{breed}</span>
        <span className={styles.breedEntryCount}>
          {scores.length} entr{scores.length === 1 ? "y" : "ies"}
        </span>
      </div>
      {males.length > 0 && (
        <div className={styles.classSection}>
          <div className={styles.classSectionTitle}>{title} Male</div>
          <PlacementGrid scores={males} value={malePlacements} onChange={changeMale} />
        </div>
      )}
      {females.length > 0 && (
        <div className={styles.classSection}>
          <div className={styles.classSectionTitle}>{title} Female</div>
          <PlacementGrid scores={females} value={femalePlacements} onChange={changeFemale} />
        </div>
      )}
      <div className={styles.sectionDivider}>Best of Breed</div>
      <div className={styles.awardGrid}>
        <AwardDropdown
          label={`Best of Breed ${title}`}
          scores={preferScores(scores, [malePlacements[0], femalePlacements[0]])}
          value={bestOfBreed}
          onChange={changeBob}
        />
      </div>
      <SaveNote status={saveStatus} />
    </div>
  );
};

export default NonLicensedBreedCard;
